import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { UserRole } from "@atsready/shared";
import { adminAuthClient as authClient } from "@/lib/auth/admin-auth-client";
import { clearBlogCmsSession } from "@/lib/blog-cms/session-logout";

interface User {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
  role: UserRole;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// The module-level auth client (token storage and auth API calls) lives in
// `@/lib/auth/admin-auth-client`, so the api client can share its single-flight refresh.

/** Returns the current in-memory access token (used by admin-api request helper). */
export function getAccessToken(): string | null {
  return authClient.getAccessToken();
}

/** Sets the in-memory access token (called after OAuth callback). */
export function setAccessToken(token: string): void {
  authClient.setAccessToken(token);
}

/** Clears the in-memory token and session flag (called on 401 from API). */
export function clearTokens(): void {
  authClient.clearTokens();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async (): Promise<boolean> => {
    const profile = await authClient.fetchUser();
    if (profile) {
      setUser(profile as User);
      return true;
    }
    setUser(null);
    return false;
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      if (!authClient.hasSession()) {
        return;
      }

      const result = await authClient.refresh();
      if (result) {
        authClient.setAccessToken(result.accessToken);
        await fetchUser();
      }
    } catch {
      // Network errors should not clear the session flag.
    } finally {
      setLoading(false);
    }
  }, [fetchUser]);

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  function login() {
    const adminOrigin = `${window.location.protocol}//${window.location.host}`;
    window.location.href = authClient.buildLoginUrl(adminOrigin);
  }

  async function logout() {
    // U9: end the editor session first. It never rejects and waits at most 2 s.
    await clearBlogCmsSession();
    await authClient.logout();
    setUser(null);
    window.location.href = "/login";
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

if (typeof window !== "undefined" && !localStorage.getItem("_admin_token_migration_v1")) {
  localStorage.removeItem("adminRefreshToken");
  localStorage.setItem("_admin_token_migration_v1", "1");
}
